import { BACKEND_URL } from "../config/supabase.js";
import { api } from "../config/api.js";
import { recordPendingCheckout, dropPendingCheckout } from "./pendingCheckouts.js";

// ─── RAZORPAY CHECKOUT ────────────────────────────────────────────────────────
// MOP (Mode-of-Payment): maps the method the customer chose in the cart selector to
// Razorpay's `options.method` block, which restricts the checkout UI to ONLY that method.
// Returns null for anything not exactly "upi"/"card"/"netbanking" — callers must treat null
// as "no restriction" (today's behaviour, every method shown) and skip setting .method at all.
export function methodToRazorpay(payMethod) {
  if (payMethod !== "upi" && payMethod !== "card" && payMethod !== "netbanking") return null;
  const all = { upi: false, card: false, netbanking: false, wallet: false, emi: false, paylater: false };
  return { ...all, [payMethod]: true };
}

// Loads Razorpay checkout.js and opens their modal
export async function loadRazorpaySDK() {
  if (window.Razorpay) return window.Razorpay;
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(window.Razorpay);
    s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
    document.head.appendChild(s);
  });
}

export async function startRazorpayPayment({ amount, orderId, storeId, customerId, customerName, customerEmail, customerPhone, storeName, storeSlug, storeEmail, storePhone, ownerName, items, deliveryAddress, shippingCost, coupon, customerNote, feeInputs, addons, international, payMethod, storeOrders, onSuccess, onFail, onDismiss, onVerifyError, onModalOpen, onVerifying }) {
  try {
    // 1. Fire backend order creation + SDK load in parallel — cuts wait time in half
    const [res, RazorpayConstructor] = await Promise.all([
      fetch(`${BACKEND_URL}/api/rzp-create-order`, {
        method:  "POST",
        headers: { "Content-Type":"application/json" },
        body:    JSON.stringify({ amount, orderId, storeName, items, addons: Array.isArray(addons) && addons.length ? addons : null, storeId: storeOrders ? "multi" : (storeId || null), coupon: storeOrders ? null : (coupon || null), customerNote: customerNote || null, feeInputs: Array.isArray(feeInputs) ? feeInputs : null, international: !!international, payMethod: payMethod || null }),
      }),
      loadRazorpaySDK(),
    ]);
    const data = await res.json();
    if (!data.rzpOrderId) throw new Error(data.error === "PAYMENTS_PAUSED" ? "Payments are paused right now — please check back soon!" : (data.message || data.error || "Razorpay order creation failed"));

    let paymentHandled = false; // flag to avoid ondismiss firing after successful payment

    const rzpOpts = {
      key:         data.keyId,
      amount:      data.amount,          // in paise
      currency:    data.currency || "INR",
      order_id:    data.rzpOrderId,
      name:        storeName || "Bloom Store",
      description: `Order ${orderId}`,
      prefill: {
        name:    customerName  || "",
        email:   customerEmail || "",
        contact: customerPhone || "",
      },
      theme: { color: "#2F63DD" },       // Bloom pink

      // 3. On payment success — verify signature on backend, then call onSuccess
      handler: async (response) => {
        paymentHandled = true;  // prevent ondismiss from calling onFail
        onVerifying?.();        // tell UI: "Confirming your payment…"
        try {
          const verifyRes = await fetch(`${BACKEND_URL}/api/rzp-verify`, {
            method:  "POST",
            headers: { "Content-Type":"application/json" },
            body:    JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              bloomOrderId:        orderId,
              amount:              data.amount,
              storeId,
              customerId,
              storeEmail,
              storeName,
              ownerName,
              customerName,
              customerEmail,
              customerPhone,
              storePhone,
              items: items || [],
              deliveryAddress,
              shippingCost:  shippingCost || 0,
              coupon:        coupon || null,
              customerNote:  customerNote || null,
              storeOrders:   storeOrders || null,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            onSuccess({ paymentId: response.razorpay_payment_id, amount });
          } else {
            // Payment was captured by Razorpay but verify returned an error.
            // DO NOT cancel the order — money was taken. Use onVerifyError so
            // the caller can show a friendly message without cancelling.
            (onVerifyError || onFail)(verifyData.error || "Payment verification failed");
          }
        } catch(e) {
          // Network error during verify — payment was already captured (paymentHandled=true).
          // Must NOT cancel the order. Delegate to onVerifyError.
          (onVerifyError || onFail)("Payment verification error: " + e.message);
        }
      },

      modal: {
        // Razorpay fires ondismiss even after successful payment (when modal auto-closes).
        // If payment wasn't handled, the user closed the modal — but on UPI the payment
        // can STILL complete in their banking app afterwards. So a dismiss is NOT a
        // failure: route it to onDismiss, which must NOT release the hold (releasing
        // would free the item and risk overselling when the payment lands). Only a real
        // payment.failed below releases. The hold otherwise self-expires (30-min TTL).
        ondismiss: () => { if (!paymentHandled) (onDismiss || onFail)("Payment cancelled"); },
      },
    };
    // MOP: restrict the checkout UI to the method the customer chose (null/unknown → every
    // method shown, today's behaviour). methodToRazorpay(payMethod) resolved and applied here,
    // BEFORE the Razorpay instance is constructed, so it takes effect immediately.
    const mopMethod = methodToRazorpay(payMethod);
    if (mopMethod) rzpOpts.method = mopMethod;
    const rzp = new RazorpayConstructor(rzpOpts);

    rzp.on("payment.failed", (response) => {
      // Genuine, confirmed failure — safe to cancel + release the hold immediately.
      if (!paymentHandled) onFail(response.error?.description || "Payment failed");
    });

    rzp.open();
    onModalOpen?.(); // tell UI: modal is open, update message
  } catch(e) {
    console.error("Razorpay error:", e);
    // rzp-create-order failed (sold out, paused, network error) — stock was never
    // reserved on the backend, so do NOT release it. Pass stockReserved:false so
    // the caller's onFail handler skips the releaseStock call.
    onFail(e.message, { stockReserved: false });
  }
}

// ─── HOSTED CHECKOUT (full-page redirect) ────────────────────────────────────
// dm2buy/Cashfree-style flow: instead of the in-page checkout.js modal (which is
// flaky inside Instagram's in-app browser), navigate the WHOLE page to Razorpay's
// hosted checkout. On payment, Razorpay POSTs the result to our backend callback,
// which verifies + confirms + redirects to /order-success (or /payment-failed).
// No JS handler is involved — exactly the path Razorpay recommends for WebViews.

// Hosted checkout is now the DEFAULT for everyone. A customer can opt back to the old
// in-page modal with ?pay=modal (instant per-browser rollback); ?pay=hosted re-enables.
// A global revert is a one-line change here (flip the default) + redeploy.
export function isHostedPay() {
  try { return localStorage.getItem("bloom_pay") !== "modal"; } catch { return true; }
}
export function syncHostedPayFlag() {
  try {
    const p = new URLSearchParams(window.location.search).get("pay");
    if (p === "modal") localStorage.setItem("bloom_pay", "modal");
    else if (p === "hosted") localStorage.removeItem("bloom_pay");
  } catch { /* ignore */ }
}

// Create the order (reserves stock), then submit a form-POST to Razorpay's hosted
// checkout — a real full-page navigation. `orderIds` = every bloom order in this
// checkout (1 for single/buy-now, N for multi-store cart); the primary is orderIds[0].
// onError is called only for a PRE-redirect failure (sold out / paused / network);
// on success the page navigates away entirely.
export async function startHostedRazorpayCheckout({
  amount, orderId, orderIds, storeId, storeName, items, coupon, customerNote, feeInputs, addons, international, payMethod,
  customerName, customerEmail, customerPhone, onError, onSoldOut,
}) {
  try {
    const ids = (Array.isArray(orderIds) && orderIds.length) ? orderIds.filter(Boolean) : [orderId];
    // Create order (reserves stock) + ensure the SDK is loaded, in parallel.
    const [res, RazorpayConstructor] = await Promise.all([
      fetch(`${BACKEND_URL}/api/rzp-create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount, orderId, storeName, items,
          addons: Array.isArray(addons) && addons.length ? addons : null,
          storeId: storeId || null,
          coupon: coupon || null,
          customerNote: customerNote || null,
          orderIds: ids,
          feeInputs: Array.isArray(feeInputs) ? feeInputs : null,
          international: !!international,
          payMethod: payMethod || null,
        }),
      }),
      loadRazorpaySDK(),
    ]);
    const data = await res.json();
    if (!data.rzpOrderId) {
      // Some items couldn't be reserved — hand the full list to the caller so it can
      // show exactly what's sold out and let the customer drop them and continue.
      // (No holds remain — the backend rolled them all back before returning.)
      if (Array.isArray(data.unavailable) && data.unavailable.length && onSoldOut) {
        onSoldOut(data.unavailable, { retryable: !!data.retryable, message: data.error });
        return;
      }
      throw new Error(data.error === "PAYMENTS_PAUSED"
        ? "Payments are paused right now — please check back soon!"
        : (data.message || data.error || "Could not start payment"));
    }

    const idsParam    = ids.join(",");
    // Record exactly what is being bought so the cart can be cleaned once these orders are paid, even if
    // the customer never reaches /order-success in this browser (see utils/pendingCheckouts.js).
    recordPendingCheckout({ ids, items });
    // Pass our origin so the backend returns the customer to THIS exact domain after
    // payment (cart/session/flag are per-origin) — not the backend's FRONTEND_URL default.
    const callbackUrl = `${BACKEND_URL}/api/rzp-callback?o=${encodeURIComponent(orderId)}&ids=${encodeURIComponent(idsParam)}&r=${encodeURIComponent(window.location.origin)}`;

    // Razorpay fires modal.ondismiss when the sheet closes — INCLUDING right after a
    // successful payment (handler runs, then the modal auto-closes → ondismiss). Without
    // this guard, a successful desktop payment would close the modal, fire goFailed, and
    // redirect to /payment-failed, overriding the success redirect. Set on success first.
    let paymentHandled = false;
    const goSuccess = () => {
      paymentHandled = true;
      try { localStorage.removeItem("bloom_abandon_ids"); } catch { /* ignore */ }
      window.location.assign(`${window.location.origin}/order-success?order=${encodeURIComponent(orderId)}`);
    };
    const goFailed = () => {
      if (paymentHandled) return; // payment already succeeded — ignore the post-success dismiss
      try { api.releaseStock(ids); } catch { /* non-fatal */ }
      dropPendingCheckout(ids);
      // Payment abandoned/failed → drop the post-payment cart markers so they can't wrongly
      // clear the cart (or remove a Buy-Now item) on a LATER successful order.
      try { localStorage.removeItem("bloom_abandon_ids"); localStorage.removeItem("bloom_purchased_items"); localStorage.removeItem("bloom_pending_clear_cart"); } catch { /* ignore */ }
      window.location.assign(`${window.location.origin}/payment-failed?order=${encodeURIComponent(orderId)}&ids=${encodeURIComponent(idsParam)}`);
    };

    // checkout.js with the rich UI (UPI app buttons + timer). Pick the return mechanism
    // by DEVICE. Real standalone browsers (desktop AND real mobile Chrome/Safari) get the
    // in-page JS `handler` — the smooth one-tap UPI flow that opens GPay/PhonePe cleanly and
    // hands back. In-app WebViews (Instagram etc.) and ANY unrecognised mobile UA get the
    // full-page redirect + callback_url, which is robust in WebViews. Either way the webhook +
    // callback confirm the order server-side, so nothing is ever lost.
    //
    // SAFETY: this uses a POSITIVE real-browser allowlist, not a WebView denylist. We only
    // take the handler path when we can positively identify a genuine browser; everything
    // uncertain falls through to the redirect (unchanged from before). So an unrecognised
    // WebView can NEVER wrongly land on the handler — the exact bug that broke IG last time.
    const ua           = navigator.userAgent || "";
    // WebView markers: named in-app browsers + Android WebView tokens ("; wv)" and the
    // "Version/X.X Chrome" signature) + Google app (GSA). Real Chrome has none of these.
    const inAppWebView = /Instagram|FBAN|FBAV|FB_IAB|Line\/|MicroMessenger|Snapchat|Pinterest|musical_ly|TikTok|; wv\)|Version\/\d+\.\d+ Chrome|GSA\//i.test(ua);
    const isMobile     = /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    const realMobileBrowser = !inAppWebView && (
      // Android: a real browser engine (WebViews already excluded above).
      (/Android/i.test(ua) && /Chrome\/\d|Firefox\/\d|SamsungBrowser\/\d/i.test(ua)) ||
      // iOS: real Safari carries "Safari/"; Chrome=CriOS, Firefox=FxiOS. WKWebView omits all three.
      (/iPhone|iPad|iPod/i.test(ua) && (/Safari\//i.test(ua) || /CriOS\/\d|FxiOS\/\d/i.test(ua)))
    );
    // in-page handler for confirmed desktop OR confirmed real mobile browser; redirect for the rest.
    const useHandler   = realMobileBrowser || (!inAppWebView && !isMobile);
    const rzpOpts = {
      key:         data.keyId,
      amount:      data.amount,            // paise
      currency:    data.currency || "INR",
      order_id:    data.rzpOrderId,
      name:        storeName || "Bloom",
      description: `Order ${orderId}`,
      prefill: { name: customerName || "", email: customerEmail || "", contact: customerPhone || "" },
      theme:  { color: "#2F63DD" },
      modal:  { ondismiss: goFailed },
    };
    if (useHandler) {
      rzpOpts.handler = goSuccess;        // desktop: we drive the return + cart clear
    } else {
      rzpOpts.callback_url = callbackUrl; // mobile + in-app WebViews: robust server redirect
      rzpOpts.redirect     = true;
    }
    // MOP: restrict the checkout UI to the method the customer chose (null/unknown → every
    // method shown, today's behaviour). One assignment on the shared rzpOpts object covers
    // BOTH the handler and the redirect branch above.
    const mopMethod = methodToRazorpay(payMethod);
    if (mopMethod) rzpOpts.method = mopMethod;
    const rzp = new RazorpayConstructor(rzpOpts);
    // Safety net (WebView redirect case): if the customer hits browser-back on Razorpay's
    // page, App.jsx releases this hold when they land back on the site. Cleared on success.
    try { localStorage.setItem("bloom_abandon_ids", idsParam); } catch { /* ignore */ }
    rzp.open();
  } catch (e) {
    console.error("Hosted checkout error:", e);
    (onError || ((m) => alert(m)))(e.message || "Could not start payment");
  }
}
